import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import {
  TemplatesClient,
  TemplateListDto,
  CreateTemplateCommand,
  UpdateTemplateCommand,
  TemplateTaskDto,
  TemplateTaskListDto,
  TemplateTasksClient,
  RequestType,
  TemplateCategoriesClient,
  TemplateCategoryDto
} from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { DataTable } from '../../components/ui/data-table';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { TeamDto } from '../../web-api-client';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { getErrorMessage } from '../../utils/errorHelpers';

// Modal component - extracted outside to prevent re-creation on every render
interface ModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: string;
}

const Modal: React.FC<ModalProps> = ({ show, onClose, title, children, size = 'max-w-2xl' }) => {
  useEffect(() => {
    if (show) {
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  if (!show) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not on children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" aria-hidden="true" />
        <div
          className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl ${size} w-full max-h-[90vh] flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function Templates() {
  const [templates, setTemplates] = useState<TemplateListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTaskSelectionModal, setShowTaskSelectionModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListDto | null>(null);
  const [formData, setFormData] = useState({
    templateName: '',
    description: '',
    requestType: RequestType.Onboarding
  });
  const [submitting, setSubmitting] = useState(false);

  // Available tasks from the bank
  const [availableTasks, setAvailableTasks] = useState<TemplateTaskListDto[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<TemplateTaskListDto[]>([]);
  const [tempSelectedTasks, setTempSelectedTasks] = useState<TemplateTaskListDto[]>([]); // Temporary selection for modal
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<number>>(new Set());

  // Categories for display
  const [categories, setCategories] = useState<TemplateCategoryDto[]>([]);

  // Users and teams for assignee display
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);

  // Define columns using ColumnDef
  const columns: ColumnDef<TemplateListDto>[] = [
    {
      accessorKey: 'templateName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="hover:bg-transparent p-0"
          >
            Template Name
            {column.getIsSorted() === "asc" && <ArrowUpIcon className="ml-2 h-4 w-4" />}
            {column.getIsSorted() === "desc" && <ArrowDownIcon className="ml-2 h-4 w-4" />}
          </Button>
        )
      },
      cell: ({ row }) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
            <DocumentTextIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {row.getValue('templateName')}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
          {row.getValue('description') || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'requestType',
      header: 'Type',
      cell: ({ row }) => {
        const requestType = row.getValue('requestType') as RequestType;
        const colors = {
          [RequestType.Onboarding]: 'bg-green-100 text-green-800',
          [RequestType.Offboarding]: 'bg-red-100 text-red-800'
        };
        const badgeColor = requestType !== undefined ? colors[requestType] || 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800';
        return (
          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${badgeColor}`}>
            {requestType !== undefined ? RequestType[requestType] : 'Unknown'}
          </span>
        );
      },
    },
    {
      accessorKey: 'taskCount',
      header: 'Tasks',
      cell: ({ row }) => (
        <div className="text-sm text-gray-900 dark:text-white">
          {row.getValue('taskCount')} tasks
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row.original)}
            className="text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit template"
          >
            <PencilIcon className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteClick(row.original)}
            className="text-red-600 hover:text-red-900 hover:bg-red-50"
            title="Delete template"
          >
            <TrashIcon className="h-5 w-5" />
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchTemplates();
    fetchAvailableTasks();
    fetchCategories();
    fetchUsers();
    fetchTeams();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const client = new TemplatesClient(API_CONFIG.baseUrl);
      const data = await client.getTemplatesList();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTasks = async () => {
    try {
      const client = new TemplateTasksClient(API_CONFIG.baseUrl);
      const data = await client.getTemplateTasks(undefined, undefined, true);
      setAvailableTasks(data);
    } catch (error) {
      console.error('Failed to fetch available tasks:', error);
      toast.error('Failed to load task bank');
    }
  };

  const fetchCategories = async () => {
    try {
      const client = new TemplateCategoriesClient(API_CONFIG.baseUrl);
      const data = await client.getTemplateCategories(true);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const teamsData = await teamsService.getTeams();
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const handleEdit = async (template: TemplateListDto) => {
    setSelectedTemplate(template);
    setFormData({
      templateName: template.templateName || '',
      description: template.description || '',
      requestType: template.requestType || RequestType.Onboarding
    });

    // Fetch template tasks
    try {
      const client = new TemplateTasksClient(API_CONFIG.baseUrl);
      const tasks = await client.getTemplateTasks(template.id, undefined, true);
      setSelectedTasks(tasks);
    } catch (error) {
      console.error('Failed to fetch template tasks:', error);
      toast.error('Failed to load template tasks');
    }

    setShowEditModal(true);
  };

  const handleDeleteClick = (template: TemplateListDto) => {
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.templateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (selectedTasks.length === 0) {
      toast.error('Please select at least one task from the bank');
      return;
    }

    try {
      setSubmitting(true);
      const client = new TemplatesClient(API_CONFIG.baseUrl);

      // Convert selected tasks to TemplateTaskDto with IDs
      const taskDtos = selectedTasks.map((task, index) => new TemplateTaskDto({
        id: task.id,
        taskName: task.taskName,
        description: task.description,
        displayOrder: index + 1,
        requiresApproval: task.requiresApproval,
        approverEmail: task.approverEmail,
        defaultPicId: task.defaultPicId,
        defaultTeamId: task.defaultTeamId,
        templateCategoryId: task.templateCategoryId,
        isActive: true
      }));

      if (showEditModal && selectedTemplate) {
        // Update
        const command = new UpdateTemplateCommand({
          id: selectedTemplate.id!,
          templateName: formData.templateName,
          description: formData.description || undefined,
          requestType: formData.requestType,
          tasks: taskDtos
        });
        await client.updateTemplate(selectedTemplate.id!, command);
        toast.success('Template updated successfully');
      } else {
        // Create
        const command = new CreateTemplateCommand({
          templateName: formData.templateName,
          description: formData.description || undefined,
          requestType: formData.requestType,
          templateTasks: taskDtos
        });
        await client.createTemplate(command);
        toast.success('Template created successfully');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
      fetchTemplates();
    } catch (error: unknown) {
      console.error('Failed to save template:', error);
      toast.error(getErrorMessage(error, 'Failed to save template'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      setSubmitting(true);
      const client = new TemplatesClient(API_CONFIG.baseUrl);
      await client.deleteTemplate(selectedTemplate.id!);
      toast.success('Template deleted successfully');
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error: unknown) {
      console.error('Failed to delete template:', error);
      toast.error(getErrorMessage(error, 'Failed to delete template'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      templateName: '',
      description: '',
      requestType: RequestType.Onboarding
    });
    setSelectedTasks([]);
    setSelectedTemplate(null);
    setTaskSearchQuery('');
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openTaskSelectionModal = () => {
    // Initialize temp selection with current selected tasks
    setTempSelectedTasks([...selectedTasks]);
    setShowTaskSelectionModal(true);
  };

  const handleTaskSelection = (task: TemplateTaskListDto) => {
    const isSelected = tempSelectedTasks.some(t => t.id === task.id);
    if (isSelected) {
      setTempSelectedTasks(tempSelectedTasks.filter(t => t.id !== task.id));
    } else {
      setTempSelectedTasks([...tempSelectedTasks, task]);
    }
  };

  const handleRemoveTask = (taskId: number | undefined) => {
    if (!taskId) return;
    setSelectedTasks(selectedTasks.filter(t => t.id !== taskId));
    toast.success('Task removed from template');
  };

  const confirmTaskSelection = () => {
    // Commit temp selection to actual selection
    setSelectedTasks([...tempSelectedTasks]);
    setShowTaskSelectionModal(false);
    setTaskSearchQuery('');
    toast.success(`${tempSelectedTasks.length} task(s) selected`);
  };

  const cancelTaskSelection = () => {
    // Discard temp selection
    setTempSelectedTasks([]);
    setShowTaskSelectionModal(false);
    setTaskSearchQuery('');
  };

  const toggleCategoryCollapse = (categoryId: number) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Filter available tasks based on search
  const filteredAvailableTasks = availableTasks.filter(task =>
    task.taskName?.toLowerCase().includes(taskSearchQuery.toLowerCase()) ||
    task.description?.toLowerCase().includes(taskSearchQuery.toLowerCase()) ||
    task.templateCategoryName?.toLowerCase().includes(taskSearchQuery.toLowerCase())
  );

  // Get assignee name (person or team)
  const getAssigneeName = (task: TemplateTaskListDto): string | null => {
    if (task.defaultPicId) {
      const user = users.find(u => u.id === task.defaultPicId);
      return user ? `${user.userName} (Person)` : null;
    }
    if (task.defaultTeamId) {
      const team = teams.find(t => t.id === task.defaultTeamId);
      return team ? `${team.name} (Team)` : null;
    }
    return null;
  };

  // Group selected tasks by category
  const getGroupedTasks = () => {
    const categorized: { [key: number]: { category: TemplateCategoryDto; tasks: TemplateTaskListDto[] } } = {};

    selectedTasks.forEach((task) => {
      if (task.templateCategoryId) {
        if (!categorized[task.templateCategoryId]) {
          const category = categories.find(c => c.id === task.templateCategoryId);
          if (category) {
            categorized[task.templateCategoryId] = { category, tasks: [] };
          }
        }
        if (categorized[task.templateCategoryId]) {
          categorized[task.templateCategoryId].tasks.push(task);
        }
      }
    });

    return Object.values(categorized);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Templates</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage onboarding and offboarding templates by selecting tasks from the task bank
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Create Template
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={templates}
            searchable={true}
            searchColumn="templateName"
            searchPlaceholder="Search templates..."
          />
        )}
      </div>

      {/* Add/Edit Template Modal */}
      <Modal
        show={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        title={showEditModal ? 'Edit Template' : 'Create Template'}
        size="max-w-4xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">
                Template Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="templateName"
                type="text"
                value={formData.templateName}
                onChange={e => setFormData({ ...formData, templateName: e.target.value })}
                placeholder="Enter template name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Enter template description (optional)"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestType">
                Request Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.requestType.toString()}
                onValueChange={(value) => setFormData({ ...formData, requestType: parseInt(value) as RequestType })}
                disabled={showEditModal}
              >
                <SelectTrigger id="requestType">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RequestType.Onboarding.toString()}>Onboarding</SelectItem>
                  <SelectItem value={RequestType.Offboarding.toString()}>Offboarding</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                Template Tasks ({selectedTasks.length})
              </h4>
              <button
                type="button"
                onClick={openTaskSelectionModal}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Select Tasks
              </button>
            </div>

            {selectedTasks.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No tasks selected. Click "Select Tasks" to add tasks to this template.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Display tasks grouped by category */}
                {getGroupedTasks().map(({ category, tasks }) => (
                  <div key={category.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {category.name}
                    </h5>
                    <div className="space-y-2">
                      {tasks.map(task => (
                        <div
                          key={task.id}
                          className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm flex items-center justify-between"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {task.taskName}
                            </p>
                            {task.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {task.description}
                              </p>
                            )}
                            {task.requiresApproval && (
                              <p className="text-xs text-blue-600 mt-1">
                                Requires Approval: {task.approverEmail}
                              </p>
                            )}
                            {getAssigneeName(task) && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Assigned to: {getAssigneeName(task)}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className="ml-4 text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : showEditModal ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Task Selection Modal */}
      <Modal
        show={showTaskSelectionModal}
        onClose={() => setShowTaskSelectionModal(false)}
        title="Select Tasks"
        size="max-w-4xl"
      >
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              value={taskSearchQuery}
              onChange={e => setTaskSearchQuery(e.target.value)}
              placeholder="Search tasks by name, description, or category..."
              className="pl-10"
            />
          </div>

          {/* Selected Count */}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">{tempSelectedTasks.length}</span> task(s) currently selected
          </div>

          {/* Task List - Grouped by Categories */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
            {filteredAvailableTasks.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                No tasks found in the bank
              </div>
            ) : (
              <div>
                {/* Group tasks by category */}
                {categories
                  .filter(category =>
                    filteredAvailableTasks.some(task => task.templateCategoryId === category.id)
                  )
                  .map((category) => {
                    const categoryTasks = filteredAvailableTasks.filter(
                      task => task.templateCategoryId === category.id
                    );

                    return (
                      <div key={category.id}>
                        {/* Category Header */}
                        <div
                          className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600 sticky top-0 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-between"
                          onClick={() => toggleCategoryCollapse(category.id!)}
                        >
                          <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                            {collapsedCategories.has(category.id!) ? (
                              <ChevronRightIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )}
                            {category.name} ({categoryTasks.length})
                          </h6>
                        </div>

                        {/* Tasks in this category */}
                        {!collapsedCategories.has(category.id!) && (
                          <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {categoryTasks.map(task => {
                            const isSelected = tempSelectedTasks.some(t => t.id === task.id);
                            const assigneeName = getAssigneeName(task);

                            return (
                              <div
                                key={task.id}
                                onClick={() => handleTaskSelection(task)}
                                className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                  isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleTaskSelection(task);
                                    }}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer mt-0.5 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                                      {task.taskName}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      {assigneeName && (
                                        <span className="flex items-center gap-1">
                                          {task.defaultTeamId ? '👥' : '👤'} {assigneeName}
                                        </span>
                                      )}
                                      {task.requiresApproval && (
                                        <span className="flex items-center text-blue-600">
                                          <CheckIcon className="h-3 w-3 mr-1" />
                                          Approval Required
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Uncategorized tasks */}
                {filteredAvailableTasks.filter(task => !task.templateCategoryId).length > 0 && (
                  <div>
                    <div
                      className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600 sticky top-0 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-between"
                      onClick={() => toggleCategoryCollapse(-1)}
                    >
                      <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                        {collapsedCategories.has(-1) ? (
                          <ChevronRightIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                        Uncategorized ({filteredAvailableTasks.filter(task => !task.templateCategoryId).length})
                      </h6>
                    </div>
                    {!collapsedCategories.has(-1) && (
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredAvailableTasks
                        .filter(task => !task.templateCategoryId)
                        .map(task => {
                          const isSelected = tempSelectedTasks.some(t => t.id === task.id);
                          const assigneeName = getAssigneeName(task);

                          return (
                            <div
                              key={task.id}
                              onClick={() => handleTaskSelection(task)}
                              className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleTaskSelection(task);
                                  }}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer mt-0.5 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                                    {task.taskName}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {assigneeName && (
                                      <span className="flex items-center gap-1">
                                        {task.defaultTeamId ? '👥' : '👤'} {assigneeName}
                                      </span>
                                    )}
                                    {task.requiresApproval && (
                                      <span className="flex items-center text-blue-600">
                                        <CheckIcon className="h-3 w-3 mr-1" />
                                        Approval Required
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={cancelTaskSelection}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmTaskSelection}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
            >
              Confirm Selection ({tempSelectedTasks.length})
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal
        show={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedTemplate(null);
        }}
        title="Delete Template"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Are you sure you want to delete the template "<strong>{selectedTemplate?.templateName}</strong>"?
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedTemplate(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
